import React, { useMemo } from "react";
import styled from "styled-components";
import { DatePicker, Select } from "antd";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";
import { DATE_FORMAT_DD_MM_YYYY } from "../../../../../constants/DateConstant";
import locale from "antd/es/date-picker/locale/nl_NL";

const PeriodSelector = ({ period, setPeriod, startDate, setStartDate, endDate, setEndDate }) => {
  const { t } = useTranslation();

  const getPeriodToDays = (period) => {
    switch (period) {
      case "week":
        return 7;
      case "month":
        return 30;
      case "quarter":
        return 90;
      case "halfyear":
        return 180;
      case "year":
        return 365;
      default:
        return 0;
    }
  };

  const formattedPeriod = useMemo(() => {
    if (!startDate && !endDate && !period) return "";
    let start = startDate;
    let end = endDate;
    if (!startDate) {
      start = dayjs().subtract(getPeriodToDays(period), "days").startOf("day");
    }
    if (!endDate) {
      end = dayjs().subtract(1, "day").endOf("day");
    }
    return `${dayjs(start).format("DD MMM YYYY")} - ${dayjs(end).format("DD MMM YYYY")}`;
  }, [period, startDate, endDate]);

  const handlePeriodChange = (value) => {
    setPeriod(value);
    setStartDate("");
    setEndDate("");
  };

  return (
    <>
      <FormGroup>
        <Label>{t("period")}</Label>
        <Select
          value={period}
          onChange={handlePeriodChange}
          options={[
            { value: "week", label: t("past_week") },
            { value: "month", label: t("past_month") },
            { value: "quarter", label: t("past_quarter") },
            { value: "halfyear", label: t("past_half_year") },
            { value: "year", label: t("past_year") },
            { value: "custom", label: t("custom_period") },
          ]}
          style={{ width: "100%", minWidth: "200px" }}
        />
        <p style={{ marginTop: 4, marginBottom: 0, color: "grey", fontStyle: "italic" }}>{formattedPeriod}</p>
      </FormGroup>

      {period === "custom" && (
        <>
          <FormGroup>
            <Label>{t("start_date")}</Label>
            <DatePicker
              value={startDate ?? null}
              onChange={(date) => setStartDate(date)}
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              minDate={dayjs().subtract(1, "year")}
              maxDate={dayjs().subtract(1, "day")}
            />
          </FormGroup>
          <FormGroup>
            <Label>{t("end_date")}</Label>
            <DatePicker
              value={endDate ?? null}
              onChange={(date) => setEndDate(date)}
              locale={locale}
              format={DATE_FORMAT_DD_MM_YYYY}
              minDate={dayjs().subtract(1, "year")}
              maxDate={dayjs().subtract(1, "day")}
            />
          </FormGroup>
        </>
      )}
    </>
  );
};

const Card = styled.div`
  background-color: white;
  border-radius: 0.5rem;
  box-shadow:
    0 1px 3px 0 rgba(0, 0, 0, 0.1),
    0 1px 2px 0 rgba(0, 0, 0, 0.06);
  padding: 1rem;
  margin-bottom: 1.5rem;
`;

const FormGroup = styled.div`
  margin-bottom: 1rem;
`;

const Label = styled.label`
  display: block;
  font-size: 0.875rem;
  font-weight: 500;
  color: #4b5563;
  margin-bottom: 0.25rem;
`;

const FlexContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
`;

export default PeriodSelector;
